/**
 * CloudWatch RUM client — initialized once at module scope (not in useEffect)
 * to avoid double-init under React StrictMode.
 */
import config from '@/config';
import { RumConsoleCapturePlugin } from '@/rum/RumConsoleCapturePlugin';
import { RumNetworkContentPlugin } from '@/rum/RumNetworkContentPlugin';

import { AwsRum, type AwsRumConfig, RRWebPlugin } from 'aws-rum-web';

import { logger } from '@/utils/logger';
import { auth0SubToRumUserId } from '@/utils/rumUserId';

const APPLICATION_VERSION = '1.0.0';

const isRumConfigured = (): boolean =>
  config.RUM_ENABLED &&
  Boolean(config.RUM_APP_ID) &&
  Boolean(config.RUM_IDENTITY_POOL_ID) &&
  Boolean(config.RUM_REGION);

const createAwsRum = (): AwsRum | null => {
  if (!isRumConfigured()) {
    return null;
  }

  const eventPluginsToLoad = [
    new RumNetworkContentPlugin(),
    new RumConsoleCapturePlugin(),
    ...(config.SESSION_REPLAY_ENABLED ? [new RRWebPlugin({ additionalSampleRate: 1 })] : []),
  ];

  const rumConfig: AwsRumConfig = {
    sessionSampleRate: 1,
    identityPoolId: config.RUM_IDENTITY_POOL_ID,
    endpoint: `https://dataplane.rum.${config.RUM_REGION}.amazonaws.com`,
    telemetries: [
      'performance',
      'errors',
      [
        'http',
        {
          recordAllRequests: true,
        },
      ],
    ],
    allowCookies: true,
    debug: !config.IS_PRODUCTION,
    enableXRay: false,
    signing: true,
    disableAutoPageView: true,
    recordResourceUrl: false,
    eventPluginsToLoad,
    // @ghana7989: setting this to 0 is gonna remove the limit on the number of events that can be sent in a session.
    sessionEventLimit: 0,
  };

  try {
    return new AwsRum(config.RUM_APP_ID, APPLICATION_VERSION, config.RUM_REGION, rumConfig);
  } catch (error) {
    logger.error('[RUM] Failed to initialize CloudWatch RUM client', error);
    return null;
  }
};

export const awsRum = createAwsRum();

export const isRumActive = (): boolean => awsRum !== null;

export const recordRumError = (error: Error): void => {
  awsRum?.recordError(error);
};

export const recordRumPageView = (pageId: string): void => {
  awsRum?.recordPageView({ pageId });
};

export const setRumUserId = (userId: string): void => {
  if (!userId) return;
  awsRum?.pinUserId(userId);
};

/** Map Auth0 sub to a stable RUM-compatible UUID, then pin it on the session. */
export const setRumUserFromAuth0Sub = async (auth0Sub: string): Promise<void> => {
  if (!auth0Sub || !awsRum) return;
  try {
    const rumUserId = await auth0SubToRumUserId(auth0Sub);
    awsRum.pinUserId(rumUserId);
  } catch (error) {
    logger.error('[RUM] Failed to set user ID from Auth0 sub', error);
  }
};

export const setRumSessionAttributes = (
  attributes: Record<string, string | number | boolean>
): void => {
  if (Object.keys(attributes).length === 0) return;
  awsRum?.addSessionAttributes(attributes);
};

export const startNewRumSession = (): void => {
  awsRum?.startSession();
};

export const getRumSessionId = (): string | null => awsRum?.getSessionId() ?? null;

export const getRumUserId = (): string | null => awsRum?.getUserId() ?? null;
